import { isHotkey } from 'is-hotkey';
import { useEffect, useRef } from 'react';

import { type BindableShortcutId, SHORTCUTS, type ShortcutDefinition } from './registry';

type UseShortcutOptions = {
    enabled?: boolean;
    preventDefault?: boolean;
    ignoreWhenTyping?: boolean;
};

const isEditableTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    const tagName = target.tagName;

    return (
        tagName === 'INPUT' ||
        tagName === 'TEXTAREA' ||
        tagName === 'SELECT' ||
        target.isContentEditable
    );
};

const matchesShortcut = (definition: ShortcutDefinition, event: KeyboardEvent): boolean => {
    if (
        definition.eventKey !== undefined &&
        event.key === definition.eventKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
    ) {
        return true;
    }

    return isHotkey(definition.keys, event);
};

export const useShortcut = (
    id: BindableShortcutId,
    handler: (event: KeyboardEvent) => void,
    options: UseShortcutOptions = {},
): void => {
    const { enabled = true, preventDefault = true, ignoreWhenTyping = false } = options;
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const definition: ShortcutDefinition = SHORTCUTS[id];

        const listener = (event: KeyboardEvent) => {
            if (!matchesShortcut(definition, event)) {
                return;
            }

            if (ignoreWhenTyping && isEditableTarget(event.target)) {
                return;
            }

            if (preventDefault) {
                event.preventDefault();
            }

            handlerRef.current(event);
        };

        document.addEventListener('keydown', listener);

        return () => {
            document.removeEventListener('keydown', listener);
        };
    }, [id, enabled, preventDefault, ignoreWhenTyping]);
};
